import { createFileRoute } from '@tanstack/react-router'
import { FolderPlus, Link2, Plus } from 'lucide-react'
import { useCallback, useState } from 'react'

import { isCalculationBranch, isFolderedKind } from '@brickup/shared/constants'
import { CATALOGUE_KINDS, type CatalogueKind } from '@brickup/shared/enums'
import { CATALOGUE } from '@brickup/shared/locales/de/catalogue'
import { CATALOGUE_KIND_LABELS } from '@brickup/shared/locales/de/enum-labels'
import { NAV } from '@brickup/shared/locales/de/nav'
import { Button, ReadOnlyScope } from '@/components/base'
import {
  CATALOGUE_KIND_FILTER_DEFAULTS,
  catalogueListFilters,
} from '@/components/Internal/Catalogue/catalogue-filters'
import { CatalogueDetail } from '@/components/Internal/Catalogue/CatalogueDetail'
import { CatalogueTree } from '@/components/Internal/Catalogue/CatalogueTree'
import { ProductImportModal } from '@/components/Internal/Catalogue/ProductImportModal'
import { useCreateCatalogueEntry } from '@/components/Internal/Catalogue/use-create-catalogue-entry'
import { useNewFolder } from '@/components/Internal/Catalogue/use-new-folder'
import {
  listSearchValidator,
  useListFilters,
} from '@/components/Internal/DataTable'
import { MasterDetailLayout } from '@/components/Internal/MasterDetailLayout'
import { PageLayout } from '@/components/Internal/PageLayout'
import { useCanEditCalculation } from '@/hooks/use-calculation-access'

const KIND_TABS = CATALOGUE_KINDS.map(kind => ({
  id: kind,
  label: CATALOGUE_KIND_LABELS[kind],
}))

export const Route = createFileRoute('/internal/catalogue')({
  validateSearch: listSearchValidator(catalogueListFilters),
  component: CatalogueScreen,
})

/**
 * The catalogue.
 *
 * Which catalogue is open, which row is selected and how the list is filtered
 * all live in the URL rather than in component state, so a formula's "used by"
 * list can link straight at the measure that reads it — and so a reload lands
 * where the user was.
 */
function CatalogueScreen() {
  const filters = useListFilters(catalogueListFilters, Route)
  const { kind, selected } = filters.values
  const [importing, setImporting] = useState(false)

  const select = useCallback(
    (id: number | null) => filters.set('selected', id),
    [filters],
  )

  // A kind-specific filter means nothing in another catalogue, so switching
  // widens all three along with closing whatever detail pane was open.
  const changeKind = useCallback(
    (next: CatalogueKind) =>
      filters.setMany({
        kind: next,
        selected: null,
        ...CATALOGUE_KIND_FILTER_DEFAULTS,
      }),
    [filters],
  )

  const createCatalogueEntry = useCreateCatalogueEntry(kind, select)
  const newFolder = useNewFolder(kind)
  const canEditCalculation = useCanEditCalculation()
  // Only the branches that hold what a price is computed by — the same record
  // the server refuses writes by. A role that reads the catalogue without
  // owning it is already in a read-only scope, which `InternalLayout` sets
  // from `AREAS.writeRoles`.
  const readOnly = isCalculationBranch(kind) && !canEditCalculation

  return (
    <PageLayout
      title={NAV.catalogue}
      tabs={KIND_TABS}
      activeTab={kind}
      onTabChange={id => changeKind(id as CatalogueKind)}
    >
      <ReadOnlyScope readOnly={readOnly}>
        <MasterDetailLayout
          storageKey="split:catalogue"
          sidebar={
            selected != null ? (
              <CatalogueDetail
                kind={kind}
                id={selected}
                onClose={() => select(null)}
              />
            ) : undefined
          }
        >
          {/* Keyed by kind: the expanded folders and the ticked rows are ids of
            the catalogue they were opened in, and mean nothing in the next. */}
          <CatalogueTree
            key={kind}
            kind={kind}
            filters={filters}
            onCreate={createCatalogueEntry}
            actions={
              <>
                {/* A product and an Arbeitsleistung are filed by their
                  classification, so there is no folder to make for them. */}
                {isFolderedKind(kind) ? (
                  <Button
                    tooltip={CATALOGUE.newFolder}
                    size="icon"
                    variant="ghost"
                    onClick={() => newFolder()}
                    icon={FolderPlus}
                  />
                ) : null}

                {kind === 'PRODUCT' ? (
                  <Button
                    tooltip={CATALOGUE.product.importFromLink}
                    size="icon"
                    variant="ghost"
                    onClick={() => setImporting(true)}
                    icon={Link2}
                  />
                ) : null}

                <Button
                  size="sm"
                  icon={Plus}
                  onClick={() => createCatalogueEntry(null)}
                >
                  {CATALOGUE.addByKind[kind]}
                </Button>
              </>
            }
          />
        </MasterDetailLayout>
      </ReadOnlyScope>

      <ProductImportModal
        open={importing}
        onClose={() => setImporting(false)}
        onImported={id => {
          setImporting(false)
          select(id)
        }}
      />
    </PageLayout>
  )
}
